import React, { useRef } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaQrcode } from "react-icons/fa";

const RestaurantPOSDashboard = () => {
  const maximizeBtnRef = useRef(null);
  const currentDate = new Date();
  const dayName = currentDate.toLocaleString("default", { weekday: "long" });
  const formattedDate = currentDate.toLocaleString("default", { day: "2-digit", month: "long", year: "numeric" });

  // Orders Widget: Breakdown of order types, total activity (e.g., order volume)
  const ordersStats = {
    totalActivity: "82%",
    barValues: [
      { value: "50%", color: "#32a852" }, // Dine-In
      { value: "30%", color: "#6f42c1" }, // Takeout
      { value: "20%", color: "#dc3545" }, // Delivery
    ],
    circleStats: [
      { value: 120, label: "Dine-In", color: "#32a852" },
      { value: 65, label: "Takeout", color: "#6f42c1" },
      { value: 30, label: "Delivery", color: "#dc3545" },
    ],
  };

  // Sales Widget: Revenue growth, total sales, breakdowns (e.g., completed, pending)
  const salesStats = {
    percentageChange: "15%",
    totalSales: "$3,150",
    subPercentage: "10%",
    totalGenerated: 250,
    statusCircles: [
      { label: "Completed", value: 215, color: "#6f42c1" },
      { label: "Pending", value: 25, color: "#ffc107" },
      { label: "Cancelled", value: 10, color: "#dc3545" },
    ],
  };

  // Inventory Widget: Low stock, active items, inventory value, COGS indicator
  const inventoryStats = {
    lowStock: 8,
    activeItems: 145,
    newItems: 15,
    barValues: [8, 145, 15],
    totalValue: "$450",
    activeValue: "$320",
    lowValue: "$45",
    cogsPercentage: "28%", // Added COGS metric
  };

  // Today's Orders: List with customer, time, type, status, total
  const orderData = [
    {
      customer: "Mike Johnson",
      dateTime: "13 Aug 2025, 10:00 AM",
      location: "Table 5",
      status: "Completed",
      total: "$45.50",
      orderType: "Dine-In",
    },
    {
      customer: "Eleanor Pena",
      dateTime: "13 Aug 2025, 9:30 AM",
      location: "Takeout",
      status: "Pending",
      total: "$22.00",
      orderType: "Takeout",
    },
    {
      customer: "John Doe",
      dateTime: "13 Aug 2025, 11:15 AM",
      location: "Delivery",
      status: "Preparing",
      total: "$35.75",
      orderType: "Delivery",
    },
    {
      customer: "Jane Smith",
      dateTime: "13 Aug 2025, 12:00 PM",
      location: "Table 3",
      status: "Completed",
      total: "$18.99",
      orderType: "Dine-In",
    },
  ];

  // Payments Widget: Cash flow, bank transfers, instant payouts, profit margin indicator
  const paymentsStats = {
    toBank: "$5,120.45",
    instantPayout: "$2,450.30",
    progress: 65,
    profitMargin: "35%", // Added profit margin
    averageOrderValue: "$12.60", // Added average order value
  };

  // Additional useful data: Top selling items for menu optimization
  const topSoldFoods = [
    { name: "Cheeseburger", quantity: 90, revenue: "$900.00", profit: "$450.00" },
    { name: "Margherita Pizza", quantity: 70, revenue: "$700.00", profit: "$350.00" },
    { name: "Chocolate Cake", quantity: 55, revenue: "$412.50", profit: "$206.25" },
    { name: "French Fries", quantity: 100, revenue: "$300.00", profit: "$150.00" },
    { name: "Vanilla Ice Cream", quantity: 60, revenue: "$270.00", profit: "$135.00" },
  ];

  // Low Stock Items: For inventory management
  const lowStockItems = [
    { name: "Fries", quantity: 8, reorderLevel: 20 },
    { name: "Soda", quantity: 12, reorderLevel: 50 },
    { name: "Cheese", quantity: 5, reorderLevel: 10 },
    { name: "Tomatoes", quantity: 15, reorderLevel: 30 },
    { name: "Burger Patties", quantity: 10, reorderLevel: 25 },
  ];

  // Staff Metrics: Simple overview for day-to-day (e.g., active staff, labor cost)
  const staffStats = {
    activeStaff: 12,
    totalLaborCost: "$1,200",
    productivity: "85%", // Orders per staff member or similar
    turnoverRate: "5%", // Monthly turnover
  };

  // Customer Metrics: Guest count, satisfaction, repeat rate
  const customerStats = {
    guestCount: 320,
    satisfactionScore: "4.7/5",
    repeatRate: "45%",
    qrScans: 95, // For digital ordering
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">NetShine Restaurant POS</h3>
            </div>
            <div className="col-md-8 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <button className="grid-view btn btn-link">
                    <i className="las la-redo-alt" />
                  </button>
                  <button
                    className="list-view btn btn-link"
                    id="collapse-header"
                    ref={maximizeBtnRef}
                  >
                    <i className="las la-expand-arrows-alt" />
                  </button>
                </div>
                <Link
                  to="/restaurant/new-order"
                  className="btn add-btn custom-btn"
                >
                  <i className="la la-plus-circle custom-icon" /> Add New Order
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Top Row: Orders, Sales, Inventory Widgets */}
        <div className="row mb-4">
          {/* Orders Widget */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Orders Overview</h5>
                <span>Last 7 Days ▼</span>
              </div>
              <div className="card-body">
                <h2>{ordersStats.totalActivity}</h2>
                <p>Total Order Volume</p>
                {/* Bar Chart */}
                <div className="progress mb-3" style={{ height: "5px" }}>
                  {ordersStats.barValues.map((bar, idx) => (
                    <div
                      key={idx}
                      className="progress-bar"
                      style={{ width: bar.value, backgroundColor: bar.color }}
                    ></div>
                  ))}
                </div>
                {/* Circle Stats */}
                <div className="row">
                  {ordersStats.circleStats.map((stat, idx) => (
                    <div key={idx} className="col-4 text-center">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          backgroundColor: stat.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          margin: "0 auto",
                        }}
                      >
                        {stat.value}
                      </div>
                      <p>{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sales Widget */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Sales Performance</h5>
                <span>Last 7 Days ▼</span>
              </div>
              <div className="card-body">
                <h2>{salesStats.percentageChange}</h2>
                <div className="d-flex justify-content-around">
                  <span className="badge bg-success">{salesStats.totalSales}</span>
                  <span className="badge bg-warning">{salesStats.subPercentage}</span>
                </div>
                <p>{salesStats.totalGenerated} Total Orders Processed</p>
                {/* Status Circles */}
                <div className="d-flex justify-content-around">
                  {salesStats.statusCircles.map((circle, idx) => (
                    <div key={idx} className="text-center">
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: circle.color,
                          margin: "0 auto",
                        }}
                      ></div>
                      <p>{circle.label} ({circle.value})</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Inventory Widget */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Inventory Management</h5>
                <span>Last 7 Days ▼</span>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <h2>{inventoryStats.lowStock}</h2>
                  <h2>{inventoryStats.activeItems}</h2>
                  <h2>{inventoryStats.newItems}</h2>
                </div>
                {/* Dot Bar (simplified) */}
                <div className="d-flex justify-content-between mb-3">
                  <div style={{ display: "flex" }}>
                    {[...Array(8)].map((_, i) => (
                      <div key={i} style={{ width: "5px", height: "5px", backgroundColor: "#dc3545", borderRadius: "50%", marginRight: "2px" }}></div>
                    ))}
                  </div>
                  <div style={{ display: "flex" }}>
                    {[...Array(15)].map((_, i) => (
                      <div key={i} style={{ width: "5px", height: "5px", backgroundColor: "#6f42c1", borderRadius: "50%", marginRight: "2px" }}></div>
                    ))}
                  </div>
                  <div style={{ display: "flex" }}>
                    {[...Array(5)].map((_, i) => (
                      <div key={i} style={{ width: "5px", height: "5px", backgroundColor: "#32a852", borderRadius: "50%", marginRight: "2px" }}></div>
                    ))}
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <p>Low Stock</p>
                  <p>Active Items</p>
                  <p>New Stock</p>
                </div>
                <div className="d-flex justify-content-between">
                  <h5>{inventoryStats.totalValue}</h5>
                  <h5>{inventoryStats.activeValue}</h5>
                  <h5>{inventoryStats.lowValue}</h5>
                </div>
                <p>COGS: {inventoryStats.cogsPercentage}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Row: Staff and Customer Widgets */}
        <div className="row mb-4">
          {/* Staff Metrics Widget */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Staff Performance</h5>
                <span>Today</span>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6 text-center">
                    <h2>{staffStats.activeStaff}</h2>
                    <p>Active Staff</p>
                  </div>
                  <div className="col-6 text-center">
                    <h2>{staffStats.totalLaborCost}</h2>
                    <p>Labor Cost</p>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-6 text-center">
                    <h4>{staffStats.productivity}</h4>
                    <p>Productivity</p>
                  </div>
                  <div className="col-6 text-center">
                    <h4>{staffStats.turnoverRate}</h4>
                    <p>Turnover Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Metrics Widget */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Customer Insights</h5>
                <span>Today</span>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6 text-center">
                    <h2>{customerStats.guestCount}</h2>
                    <p>Guest Count</p>
                  </div>
                  <div className="col-6 text-center">
                    <h2>{customerStats.satisfactionScore}</h2>
                    <p>Satisfaction</p>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-6 text-center">
                    <h4>{customerStats.repeatRate}</h4>
                    <p>Repeat Rate</p>
                  </div>
                  <div className="col-6 text-center">
                    <h4>{customerStats.qrScans} <FaQrcode /></h4>
                    <p>QR Scans</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Today's Orders and Payments */}
        <div className="row">
          {/* Today's Orders */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Today's Orders</h5>
                <input type="text" placeholder="Search" style={{ width: "150px" }} />
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-around mb-3">
                  <div className="text-center">
                    <h4>$3,150</h4>
                    <p>Total Revenue (250)</p>
                  </div>
                  <div className="text-center">
                    <h4>$450</h4>
                    <p>Pending (25)</p>
                  </div>
                  <div className="text-center">
                    <h4>$2,500</h4>
                    <p>Completed (215)</p>
                  </div>
                  <div className="text-center">
                    <h4>$200</h4>
                    <p>Cancelled (10)</p>
                  </div>
                </div>
                {orderData.map((order, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-2 border-bottom pb-2">
                    <img src="placeholder-avatar.jpg" alt="avatar" style={{ width: "40px", borderRadius: "50%" }} />
                    <div className="mx-2 flex-grow-1">
                      <p>{order.customer}</p>
                      <p>{order.dateTime} - {order.orderType}</p>
                    </div>
                    <p>{order.location}</p>
                    <button className={`btn btn-sm ${order.status === 'Completed' ? 'btn-success' : order.status === 'Pending' ? 'btn-warning' : 'btn-info'}`}>{order.status}</button>
                    <p className="ms-2">{order.total}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Payments Widget */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Payments & Financials</h5>
                <span>Updated Today</span>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-around">
                  <div className="text-center">
                    <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "#32a852", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                      <i className="fas fa-bank"></i>
                    </div>
                    <p>Bank Transfers</p>
                  </div>
                  <div className="text-center">
                    <h4>{paymentsStats.toBank}</h4>
                    <p>Heading to Bank</p>
                    <span>{paymentsStats.progress}%</span>
                  </div>
                  <div className="text-center">
                    <h4>{paymentsStats.instantPayout}</h4>
                    <p>Instant Payouts</p>
                    <span>{100 - paymentsStats.progress}%</span>
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="progress mt-3" style={{ height: "10px" }}>
                  <div className="progress-bar" style={{ width: `${paymentsStats.progress}%`, backgroundColor: "#32a852" }}></div>
                  <div className="progress-bar" style={{ width: `${100 - paymentsStats.progress}%`, backgroundColor: "#ffc107" }}></div>
                </div>
                <div className="mt-3 text-center">
                  <p>Profit Margin: {paymentsStats.profitMargin}</p>
                  <p>Average Order Value: {paymentsStats.averageOrderValue}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Widgets: Top Sold and Low Stock */}
        <div className="row mt-4">
          {/* Top Sold Foods */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h5>Top Selling Items</h5>
              </div>
              <div className="card-body">
                <ul className="list-group">
                  {topSoldFoods.map((food, idx) => (
                    <li key={idx} className="list-group-item d-flex justify-content-between">
                      <span>{food.name}</span>
                      <span>Qty: {food.quantity} | Rev: {food.revenue} | Profit: {food.profit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Low Stock Items */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h5>Low Stock Alerts</h5>
              </div>
              <div className="card-body">
                <ul className="list-group">
                  {lowStockItems.map((item, idx) => (
                    <li key={idx} className="list-group-item d-flex justify-content-between">
                      <span>{item.name}</span>
                      <span className="badge bg-danger">Qty: {item.quantity} (Reorder: {item.reorderLevel})</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantPOSDashboard;